import { statusMap } from '@dynamika-shared/ui/StatusPill/StatusPill';
import { Tabs } from '@dynamika-shared/ui/Tabs/Tabs';
import './QCTask.scss';
import { useQCTaskHook } from '../hooks/useQCTask.hook';
import { useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { routesPath } from '@router/routes';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { twMerge } from '@utils/twMerge';
import { QcTaskPatient } from './QCTaskPatient';
import { VisitTabLabel } from './components/VisitTabLabel';
import { VisitTabContent } from './components/VisitTabContent';
import { prepareUrl } from '@utils/routerHelpers';
import { useBreadcrumbs } from 'src/hooks/useBreadcrumbs';
import { useQCPatientVisitsDatesSlice } from '@restore/QCPatientVisitsDates/QCPatientVisitsDates.slice';
import {
  useMergedVisits,
  QCVisitExtendedWithContextDataAndDates,
} from '../hooks/useMergedVisits.hook';
import { ConfirmPositionModal } from 'src/pages/QC/QCTask/components/ConfirmPosition.modal';
import { renderSkeleton } from 'src/pages/QC/QCTask/components/renderSkeleton';
import { UnlockVisitPositionModal } from 'src/pages/QC/QCTask/components/UnlockVisitPosition.modal';

export const QCTask = () => {
  const [isReorderingMode, setIsReorderingMode] = useState(false);
  const [currentVisitId, setCurrentVisitId] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    visitId: string | null;
  }>({ isOpen: false, visitId: null });
  const [isQueueChanged, setIsQueueChanged] = useState(false);
  const [unlockModal, setUnlockModal] = useState<{
    isOpen: boolean;
    visitId: string | null;
  }>({ isOpen: false, visitId: null });
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const {
    goToTasks,
    clinicalTrialId,
    taskId,
    showModal,
    closeModal,
    changePatientStatus,
    confirmChangeStatus,
    patientData,
    directChangePatientStatus,
    selectedPatientStatus,
    patientDataIsLoading,
    visitName,
    fetchQCPatientVisits,
    patientVisitsDatesLoading,
  } = useQCTaskHook();

  const [fetchQCPatientVisitsDatesSuccess, updateVisitOrder, unlockVisit] =
    useQCPatientVisitsDatesSlice((_, actions) => [
      actions.fetchQCPatientVisitsDatesSuccess,
      actions.updateVisitOrder,
      actions.unlockVisit,
    ]);

  const visits = useMergedVisits(patientData?.visits);

  const closePopover = useCallback(() => {
    if (isReorderingMode) {
      fetchQCPatientVisits();
    }
    setIsReorderingMode(false);
    setCurrentVisitId(null);
    setIsQueueChanged(false);
  }, [isReorderingMode, fetchQCPatientVisits]);

  const confirmPosition = (
    updateVisits: typeof updateVisitOrder,
    orderedVisits: QCVisitExtendedWithContextDataAndDates[],
    clinicalTrialId?: string,
    patientDataId?: string,
    visitId?: string | null
  ) => {
    if (clinicalTrialId && patientDataId && visitId) {
      const currentVisit = orderedVisits.find(visit => visit.id === visitId);

      let previousVisit = null;

      if (currentVisit) {
        previousVisit = orderedVisits.find(v => {
          const customOrder = currentVisit?.customOrder;
          if (!customOrder) {
            return false;
          }
          return v.customOrder === customOrder - 1;
        });
      }

      updateVisits({
        clinicalTrialId,
        visitId,
        patientId: patientDataId,
        previousVisitDefinitionId: previousVisit?.visitDefinitionId ?? null,
        previousVisitId: previousVisit?.id ?? null,
      });
      setIsReorderingMode(false);
      setCurrentVisitId(null);
    }
    setConfirmModal({ isOpen: false, visitId: null });
  };

  const handleOpenPopover = useCallback((visitId: string) => {
    setCurrentVisitId(visitId);
    setIsReorderingMode(true);
  }, []);

  const skeleton = useMemo(() => renderSkeleton(taskId), [taskId]);

  const createVisitTab = (
    visit: QCVisitExtendedWithContextDataAndDates,
    index: number
  ) => {
    const isCurrentVisit = currentVisitId === visit.id;
    return {
      key: visit.taskId ?? `${index}`,
      label: (
        <VisitTabLabel
          visit={visit}
          isCurrentVisit={isCurrentVisit}
          isReorderingMode={isReorderingMode}
        />
      ),
      children: (
        <VisitTabContent
          visit={visit}
          isCurrentVisit={isCurrentVisit}
          isReorderingMode={isReorderingMode}
          currentVisitId={currentVisitId}
          wrapperRef={wrapperRef}
          isQueueChanged={isQueueChanged}
          orderedVisits={visits}
          fetchQCPatientVisitsDatesSuccess={fetchQCPatientVisitsDatesSuccess}
          handleOpenPopover={handleOpenPopover}
          closePopover={closePopover}
          setIsQueueChanged={setIsQueueChanged}
          setConfirmModal={setConfirmModal}
          setUnlockModal={setUnlockModal}
        />
      ),
      disabled: isReorderingMode || !visit.taskId,
    };
  };

  const tabs =
    patientDataIsLoading || patientVisitsDatesLoading
      ? skeleton
      : visits.map(createVisitTab);

  const navigate = useNavigate();
  useBreadcrumbs(
    [
      ['QC List', 'qcTasks'],
      [`${patientData?.patientCode ?? '...'}`, 'qcTaskInfo'],
      [visitName ?? '...', 'qcTaskInfo'],
    ],
    [clinicalTrialId, taskId]
  );

  const handleUnlockVisit = (comment: string) => {
    const visitId = unlockModal.visitId;
    if (clinicalTrialId && patientData?.id && visitId) {
      unlockVisit({
        clinicalTrialId,
        patientId: patientData.id,
        visitId,
        comment,
      });
      setUnlockModal({ isOpen: false, visitId: null });
      handleOpenPopover(visitId);
    }
  };

  return (
    <>
      <div
        ref={wrapperRef}
        data-testid='QCTask'
        className={'min-h-[calc(100%-5.6rem)] pb-10'}
      >
        <QcTaskPatient
          goToTasks={goToTasks}
          confirmChangeStatus={confirmChangeStatus}
          directChangePatientStatus={directChangePatientStatus}
        />
        <Tabs
          defaultActiveKey={taskId}
          activeKey={taskId}
          items={tabs}
          destroyInactiveTabPane
          className={twMerge(
            '[&_.disabledTab_span]:opacity-50',
            isReorderingMode && 'qc-tabs-editable'
          )}
          onChange={taskId => {
            if (taskId && clinicalTrialId) {
              navigate(
                prepareUrl(routesPath.qcTaskInfo, [clinicalTrialId, taskId])
              );
            }
          }}
        />
      </div>
      <ConfirmPositionModal
        onCancel={() => {
          setConfirmModal({ isOpen: false, visitId: null });
          fetchQCPatientVisits();
        }}
        onOk={() =>
          confirmPosition(
            updateVisitOrder,
            visits,
            clinicalTrialId,
            patientData?.id,
            confirmModal.visitId
          )
        }
        showModal={confirmModal.isOpen}
      />
      <UnlockVisitPositionModal
        showModal={unlockModal.isOpen}
        onCancel={() => setUnlockModal({ isOpen: false, visitId: null })}
        onOk={handleUnlockVisit}
      />
      <Modal
        zIndex={9999}
        width={440}
        onCancel={closeModal}
        onOk={changePatientStatus}
        okProps={{ danger: true }}
        okText='Yes'
        open={showModal}
        title='Change subject status'
      >
        <span>
          Are you sure you want to change the subject status to{' '}
          <span
            className={twMerge(
              statusMap[selectedPatientStatus],
              'capitalize bg-transparent'
            )}
          >
            {selectedPatientStatus.toLowerCase()}
          </span>
          ? You will no longer be able to update the subject status and further
          uploads will not be allowed for this subject
        </span>
      </Modal>
    </>
  );
};
